/**
 * Shared helpers for the operations area (cases, tasks, assignments, dashboard, citizens, profile).
 * Lives under features/cases because the operations area owns it; nothing here depends on a screen.
 */
import { useCallback, useEffect, useState } from 'react';
import { avisar } from '@xiltrion/ui/sonner';
import type { NombreIcono } from '@xiltrion/ui/icono';
import { ApiError } from '@/api/client';

export type NotifySeverity = 'success' | 'info' | 'warn' | 'error';
/** Flash notice (sonner via `avisar`); keeps the PrimeReact-era `notify(severity, summary, detail)` shape used across the screens. */
export function notify(severity: NotifySeverity, summary: string, detail?: string, duration = 4000) {
  const fn = severity === 'warn' ? avisar.warning : severity === 'error' ? avisar.error : severity === 'info' ? avisar.info : avisar.success;
  fn(summary, { description: detail, duration });
}

/** Human message for any thrown value. */
export function errorMessage(e: unknown, fallback = 'Ocurrió un error inesperado. Intenta de nuevo.'): string {
  if (e instanceof ApiError) {
    const fe = fieldErrors(e);
    if (fe) return Object.entries(fe).map(([k, v]) => `${k}: ${v.join(', ')}`).join(' · ');
    return e.message || fallback;
  }
  if (e instanceof Error) return e.message || fallback;
  return fallback;
}
/** 422 VALIDATION → details.fieldErrors { field: string[] } */
export function fieldErrors(e: unknown): Record<string, string[]> | null {
  if (e instanceof ApiError && e.status === 422 && e.details && typeof e.details === 'object') {
    const fe = (e.details as { fieldErrors?: Record<string, string[]> }).fieldErrors;
    if (fe && typeof fe === 'object') return fe;
  }
  return null;
}

/** Esc closes drawers: AppShell dispatches `pqrs:close-drawers` on document. */
export function useCloseOnEsc(onClose: () => void, active = true) {
  useEffect(() => {
    if (!active) return;
    const h = () => onClose();
    document.addEventListener('pqrs:close-drawers', h);
    return () => document.removeEventListener('pqrs:close-drawers', h);
  }, [onClose, active]);
}

/** localStorage-backed state; safe when storage is unavailable. */
export function useLocalStorage<T>(key: string, initial: T): [T, (v: T | ((prev: T) => T)) => void] {
  const [value, setValue] = useState<T>(() => {
    try { const raw = localStorage.getItem(key); return raw ? (JSON.parse(raw) as T) : initial; } catch { return initial; }
  });
  const set = useCallback((v: T | ((prev: T) => T)) => {
    setValue((prev) => {
      const next = typeof v === 'function' ? (v as (p: T) => T)(prev) : v;
      try { localStorage.setItem(key, JSON.stringify(next)); } catch { /* storage unavailable */ }
      return next;
    });
  }, [key]);
  return [value, set];
}

export const REASSIGN_REASON_LABEL: Record<string, string> = { VACATION: 'Vacaciones', ABSENCE: 'Ausencia', OVERLOAD: 'Carga excesiva', WRONG_SPECIALTY: 'Especialidad incorrecta', CONFLICT: 'Conflicto', ESCALATION: 'Escalamiento', OTHER: 'Otro' };
export const REASSIGN_REASONS = Object.entries(REASSIGN_REASON_LABEL).map(([value, label]) => ({ value, label }));
export const OVERRIDE_REASON_LABEL: Record<string, string> = { INCORRECT: 'Clasificación incorrecta', ADDITIONAL_INFO: 'Información adicional', ORG_RULE: 'Regla organizacional', OTHER: 'Otro' };
export const AVAILABILITY_LABEL: Record<string, string> = { AVAILABLE: 'Disponible', UNAVAILABLE: 'No disponible', VACATION: 'Vacaciones', LEAVE: 'Permiso', OFF_SHIFT: 'Fuera de turno', BLOCKED: 'Bloqueado' };
export const RELATION_KIND_LABEL: Record<string, string> = { duplicate: 'Duplicado', related: 'Relacionado', 'follow-up': 'Seguimiento', parent: 'Padre', child: 'Hijo', appeal: 'Recurso', transfer: 'Traslado' };
export const ACTOR_KIND: Record<string, { icon: NombreIcono; label: string; cls: string }> = {
  USER: { icon: 'mdi:account-outline', label: 'Usuario', cls: 'bg-primary/10 text-primary' },
  AI: { icon: 'mdi:creation-outline', label: 'IA', cls: 'bg-accent text-accent-foreground' },
  CITIZEN: { icon: 'mdi:account-voice', label: 'Ciudadano', cls: 'bg-success/15 text-success' },
  INTEGRATION: { icon: 'mdi:cog-outline', label: 'Integración', cls: 'bg-warning/20 text-foreground' },
  SYSTEM: { icon: 'mdi:robot-outline', label: 'Sistema', cls: 'bg-muted text-muted-foreground' },
};
export const ACTION_LABEL: Record<string, string> = {
  RESPOND: 'Responder', REVIEW: 'Revisar', REJECT: 'Rechazar', SIGN: 'Firmar', REQUEST_INFORMATION: 'Solicitar información', REQUEST_CORRECTION: 'Solicitar corrección',
  ASSIGN: 'Asignar', REASSIGN: 'Reasignar', ACKNOWLEDGE: 'Confirmar recepción', ESCALATE: 'Escalar', TRANSFER: 'Trasladar', ARCHIVE: 'Archivar', CLOSE: 'Cerrar', REVIEW_CLASSIFICATION: 'Revisar clasificación', GENERIC: 'Tarea',
};
export const OUTCOME_LABEL: Record<string, string> = { DONE: 'Completar', APPROVED: 'Aprobar', REJECTED: 'Rechazar', CONFIRMED: 'Confirmar' };
export const DOC_KIND_LABEL: Record<string, string> = { ORIGINAL: 'Original', PETITION: 'Petición', EMAIL: 'Correo', COMMUNICATION: 'Comunicación', DERIVED: 'Copia PDF', RESPONSE: 'Respuesta', ATTACHMENT: 'Adjunto', GENERATED: 'Generado' };
/** Copia PDF/A: «Copia con OCR del n.º 3» o «Copia PDF del n.º 3». */
/** «Versión 2 · reemplaza al n.º 4». */
export const versionLabel = (version: number, replacesOrder: number | null) => `Versión ${version}${replacesOrder ? ` · reemplaza al n.º ${replacesOrder}` : ''}`;
export const copyLabel = (method: string | null, order: number | null) => `${method === 'OCR' ? 'Copia con OCR' : 'Copia PDF'}${order ? ` del n.º ${order}` : ''}`;

export function fmtBytes(n: number | null | undefined): string {
  if (!n && n !== 0) return '—';
  if (n < 1024) return `${n} B`;
  if (n < 1024 * 1024) return `${(n / 1024).toFixed(1)} KB`;
  return `${(n / (1024 * 1024)).toFixed(1)} MB`;
}

/** Relative date group for lists ("Hoy", "Mañana", "Esta semana", …). */
export function dueGroup(dueAt: string | null | undefined): { key: string; label: string; order: number } {
  if (!dueAt) return { key: 'none', label: 'Sin fecha límite', order: 9 };
  const d = new Date(dueAt); const now = new Date();
  const startToday = new Date(now.getFullYear(), now.getMonth(), now.getDate());
  const dayMs = 86_400_000;
  const diffDays = Math.floor((d.getTime() - startToday.getTime()) / dayMs);
  if (d.getTime() < now.getTime()) return { key: 'overdue', label: 'Vencidas', order: 0 };
  if (diffDays === 0) return { key: 'today', label: 'Vencen hoy', order: 1 };
  if (diffDays === 1) return { key: 'tomorrow', label: 'Vencen mañana', order: 2 };
  if (diffDays < 7) return { key: 'week', label: 'Esta semana', order: 3 };
  return { key: 'later', label: 'Más adelante', order: 4 };
}

/** Client-side CSV export. */
export function downloadCsv(filename: string, header: string[], rows: Array<Array<string | number | null | undefined>>) {
  const esc = (v: string | number | null | undefined) => { const s = v === null || v === undefined ? '' : String(v); return /[",\n;]/.test(s) ? `"${s.replace(/"/g, '""')}"` : s; };
  const csv = [header.map(esc).join(';'), ...rows.map((r) => r.map(esc).join(';'))].join('\n');
  const blob = new Blob([`﻿${csv}`], { type: 'text/csv;charset=utf-8' });
  const url = URL.createObjectURL(blob);
  const a = document.createElement('a'); a.href = url; a.download = filename; a.click();
  setTimeout(() => URL.revokeObjectURL(url), 1000);
}

export const initials = (name: string | null | undefined) => (name ?? '?').split(' ').filter(Boolean).map((p) => p[0]).slice(0, 2).join('').toUpperCase();

/** Estados en los que el trámite ya culminó: no tiene vencimiento pendiente. */
export const FINISHED_STATUSES = ['RESOLVED', 'CLOSED', 'ARCHIVED'];
/** Fecha en que culminó el trámite (cierre o, si no se cerró aún formalmente, resolución); null si sigue en curso. */
export const finishedAt = (c: { status: string; closedAt: string | null; resolvedAt: string | null }) =>
  FINISHED_STATUSES.includes(c.status) ? (c.closedAt ?? c.resolvedAt) : null;
